import type { Ref } from 'react';
import { Box, Typography } from '@mui/material';
import { StatusChip } from './StatusChip';
import type { StatusSeverity } from './statusSeverity';

export interface DimensionAssessmentRow {
  /** Dimension label (translated: Data / Voice / Messaging). */
  label: string;
  /** Tone for the standing chip. */
  severity: StatusSeverity;
  /** Standing text (translated: On track / Warned / Over budget / Not assessed). */
  statusLabel: string;
  /** Optional secondary text under the label (e.g. "118.4 GB / 25 GB"). */
  detail?: string;
}

export interface DimensionAssessmentListProps {
  rows: DimensionAssessmentRow[];
  ref?: Ref<HTMLDivElement>;
}

/**
 * Per-dimension budget readout: one row per usage dimension (Data / Voice /
 * Messaging) with a standing `StatusChip`. Presentational and namespace-free —
 * the caller resolves the dimension labels, the standing text, and the tone
 * (from `budgetStanding`). Reused by the line-detail budget pane and the
 * My-Lines per-line cards.
 */
export function DimensionAssessmentList({ rows, ref }: DimensionAssessmentListProps) {
  return (
    <Box ref={ref}>
      {rows.map((row, i) => (
        <Box
          key={row.label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            py: 1,
            borderTop: i > 0 ? '1px solid' : 'none',
            borderColor: 'borderSubtle',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="div"
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
              }}
            >
              {row.label}
            </Typography>
            {row.detail && (
              <Typography
                component="div"
                sx={{
                  fontSize: 12,
                  color: 'text.secondary',
                  fontVariantNumeric: 'tabular-nums',
                  mt: 0.25,
                }}
              >
                {row.detail}
              </Typography>
            )}
          </Box>
          <StatusChip severity={row.severity} label={row.statusLabel} dot />
        </Box>
      ))}
    </Box>
  );
}
